import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Spinner } from '@fluentui/react-components';
import { CheckboxFilter } from './controls/CheckboxFilter';
import { VirtualizedFilter } from './controls/VirtualizedFilter';
import { ServerSearchFilter } from './controls/ServerSearchFilter';
import { DateTimeRangeFilter } from './controls/DateTimeRangeFilter';
import { BooleanFilter } from './controls/BooleanFilter';
import { NumericRangeFilter } from './controls/NumericRangeFilter';
import type { NumericFilterStats } from './controls/NumericRangeFilter';
import { dashboardsService } from '../api';
import { publicDashboardsService } from '../api/publicDashboardsService';
import { sharedDashboardsService } from '../api/sharedDashboardsService';
import type { FilterOption, FilterStats, DateRangeFilterStats } from '../api/types';
import { deriveFilterUiType } from '../api/types';
import { getCachedFilterData, setCachedFilterData } from './charts/hooks';

interface FilterControlProps {
  dashboardId: string;
  filterId: string;
  filterType: 'categorical' | 'date_range' | 'numeric' | 'boolean';
  /** Raw statistics from materialization — UI derives behavior from these */
  filterStats: FilterStats | null;
  /**
   * Backend-resolved value_type (from parameter_bindings). For date_range
   * filters this is "date" or "datetime" and selects the wire format.
   */
  valueType?: string | null;
  label: string;
  variable: string;
  defaultValue?: any;
  /** Whether this filter has been modified from its default */
  isModified?: boolean;
  onSelectionChange: (value: any) => void;
  /**
   * Called when a filter auto-applies a default on mount (e.g. date range).
   * This value feeds into chart queries but does NOT mark the filter as
   * "modified" — so no reset (X) icon appears.
   */
  onDefaultApplied?: (variable: string, value: any) => void;
  /** Called when user clicks the per-filter reset icon */
  onReset?: () => void;
  /** Called once when this filter's data is ready (loaded or errored). */
  onReady: (filterId: string) => void;
  shareToken?: string;
  sharedDashboardId?: string;
}

/**
 * Filter control — routes to the correct filter component based on
 * `filterType` and data-derived `uiType`.
 *
 * UI type is computed client-side from `filterType + filterStats`.
 *
 * Each filter reports readiness via `onReady` for the filters-before-charts gate.
 */
const FilterControl: React.FC<FilterControlProps> = ({
  dashboardId,
  filterId,
  filterType,
  filterStats,
  valueType,
  label,
  variable,
  defaultValue,
  isModified = false,
  onSelectionChange,
  onDefaultApplied,
  onReset,
  onReady,
  shareToken,
  sharedDashboardId,
}) => {
  // Derive UI type from stats (categorical only)
  const uiType = deriveFilterUiType(filterType, filterStats);

  // ── Options state (for checkbox_list and searchable_list) ────────────────
  const [options, setOptions] = useState<FilterOption[]>(() => {
    if (filterType !== 'categorical' || uiType === 'search_select') return [];
    return getCachedFilterData(dashboardId, filterId)?.options ?? [];
  });

  const [isLoading, setIsLoading] = useState(() => {
    if (filterType !== 'categorical' || uiType === 'search_select') return false;
    return getCachedFilterData(dashboardId, filterId) === null;
  });

  const [error, setError] = useState<string | null>(null);

  // ── Readiness tracking ──────────────────────────────────────────────────
  const readyReported = useRef(false);
  const reportReady = useCallback(() => {
    if (readyReported.current) return;
    readyReported.current = true;
    onReady(filterId);
  }, [onReady, filterId]);

  // Non-categorical and search_select filters are ready immediately
  useEffect(() => {
    if (filterType !== 'categorical' || uiType === 'search_select') {
      reportReady();
      return;
    }
    if (getCachedFilterData(dashboardId, filterId)) {
      reportReady();
    }
  }, [filterType, uiType, dashboardId, filterId, reportReady]);

  // ── Fetch all options (checkbox_list and searchable_list only) ──────────
  useEffect(() => {
    if (filterType !== 'categorical') return;
    if (uiType === 'search_select') return;
    if (getCachedFilterData(dashboardId, filterId)) return;

    let cancelled = false;
    const fetchOptions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = shareToken
          ? await publicDashboardsService.getFilterData(shareToken, filterId)
          : sharedDashboardId
            ? await sharedDashboardsService.getFilterData(sharedDashboardId, filterId)
            : await dashboardsService.getFilterData(dashboardId, filterId);
        if (cancelled) return;
        setOptions(response.options);
        setCachedFilterData(dashboardId, filterId, response.options);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch filter options:', err);
        setError('Failed to load filter options');
        setOptions([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          reportReady();
        }
      }
    };
    fetchOptions();
    return () => { cancelled = true; };
  }, [dashboardId, filterId, filterType, uiType, reportReady, shareToken, sharedDashboardId]);

  // ── Server search handler (for search_select) ──────────────────────────
  const handleServerSearch = useCallback(
    async (query: string) => {
      const response = shareToken
        ? await publicDashboardsService.getFilterData(shareToken, filterId, { search: query, limit: 50 })
        : sharedDashboardId
          ? await sharedDashboardsService.getFilterData(sharedDashboardId, filterId, { search: query, limit: 50 })
          : await dashboardsService.getFilterData(dashboardId, filterId, { search: query, limit: 50 });
      return response.options.map((o) => ({ key: o.key, value: o.value, text: o.text }));
    },
    [dashboardId, filterId, shareToken, sharedDashboardId]
  );

  // ── Render: Categorical ─────────────────────────────────────────────────
  if (filterType === 'categorical') {
    const currentValue = Array.isArray(defaultValue)
      ? defaultValue
      : defaultValue
        ? [defaultValue]
        : [];

    if (isLoading) {
      return (
        <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ padding: '8px' }}>
            <Spinner size="tiny" label="Loading options..." />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ padding: '8px', color: 'var(--colorPaletteRedForeground1)', fontSize: '12px' }}>
            {error}
          </div>
        </div>
      );
    }

    const mappedOptions = options.map((opt) => ({ key: opt.key, value: opt.value, text: opt.text }));

    switch (uiType) {
      case 'checkbox_list':
        return (
          <CheckboxFilter
            label={label}
            options={mappedOptions}
            value={currentValue}
            isModified={isModified}
            onReset={onReset}
            onApply={(selected) => onSelectionChange(selected)}
          />
        );

      case 'searchable_list':
        return (
          <VirtualizedFilter
            label={label}
            options={mappedOptions}
            value={currentValue}
            isModified={isModified}
            onReset={onReset}
            onApply={(selected) => onSelectionChange(selected)}
          />
        );

      case 'search_select':
        return (
          <ServerSearchFilter
            label={label}
            value={currentValue}
            isModified={isModified}
            onReset={onReset}
            onApply={(selected) => onSelectionChange(selected)}
            onSearch={handleServerSearch}
          />
        );
    }
  }

  // ── Render: Boolean ─────────────────────────────────────────────────────
  if (filterType === 'boolean') {
    const boolValue: boolean | null =
      defaultValue === true || defaultValue === 'true'
        ? true
        : defaultValue === false || defaultValue === 'false'
          ? false
          : null;

    return (
      <BooleanFilter
        label={label}
        value={boolValue}
        isModified={isModified}
        onReset={onReset}
        onApply={(value) => onSelectionChange(value)}
      />
    );
  }

  // ── Render: Numeric ─────────────────────────────────────────────────────
  if (filterType === 'numeric') {
    const numStats = filterStats as NumericFilterStats | null;
    if (!numStats || numStats.min == null || numStats.max == null) {
      return null; // No stats — hide filter
    }

    const numericValue =
      defaultValue && typeof defaultValue === 'object' && !Array.isArray(defaultValue)
        ? (defaultValue as Record<string, number>)
        : null;

    return (
      <NumericRangeFilter
        label={label}
        variable={variable}
        stats={numStats}
        value={numericValue}
        isModified={isModified}
        onReset={onReset}
        onApply={(value) => {
          if (value) {
            onSelectionChange(value);
          } else {
            onSelectionChange(null);
          }
        }}
      />
    );
  }

  // ── Render: Date range ──────────────────────────────────────────────────
  if (filterType === 'date_range') {
    const dateStats = filterStats as DateRangeFilterStats | null;

    // Restore persisted value: filterStore stores {variable_start, variable_end},
    // but DateTimeRangeFilter expects {start, end}.
    let dateValue: { start: string; end: string } | null = null;
    if (defaultValue && typeof defaultValue === 'object' && !Array.isArray(defaultValue)) {
      const obj = defaultValue as Record<string, string>;
      const s = obj[`${variable}_start`];
      const e = obj[`${variable}_end`];
      if (s && e) {
        dateValue = { start: s, end: e };
      }
    }

    // Map backend value_type → physical wire format. Defaults to "datetime"
    // for unknown/null values to preserve existing behavior.
    const physicalType: 'date' | 'datetime' = valueType === 'date' ? 'date' : 'datetime';

    return (
      <DateTimeRangeFilter
        label={label}
        physicalType={physicalType}
        stats={dateStats ?? undefined}
        value={dateValue}
        isModified={isModified}
        onReset={onReset}
        onApply={(value) => {
          if (value) {
            onSelectionChange({
              [`${variable}_start`]: value.start,
              [`${variable}_end`]: value.end,
            });
          } else {
            onSelectionChange(null);
          }
        }}
        onDefaultApplied={
          onDefaultApplied
            ? (value) => {
                if (value) {
                  onDefaultApplied(variable, {
                    [`${variable}_start`]: value.start,
                    [`${variable}_end`]: value.end,
                  });
                }
              }
            : undefined
        }
      />
    );
  }

  return null;
};

export default FilterControl;